import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { passwordLogin } from "../../features/user/UserSlice";
import { AppDispatch, RootState } from "../../store";
import { toast } from "sonner";
import { FiEye, FiEyeOff, FiMail, FiLock } from "react-icons/fi";
import { LuShieldCheck } from "react-icons/lu";
import { HiMiniLockClosed } from "react-icons/hi2";

export default function Login() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading } = useSelector((state: RootState) => state.user);

  const [form, setForm] = useState({
    emailOrMobile: (location.state as any)?.emailOrMobile || "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, "");
    setForm({ ...form, [e.target.name]: value, });
  };

  const handleLogin = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!form.emailOrMobile.trim() || !form.password.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    const result = await dispatch(passwordLogin(form));

    if (passwordLogin.fulfilled.match(result)) {
      navigate("/admin/dashboard");
    } else if (passwordLogin.rejected.match(result)) {
      toast.error((result.payload as string) || "Email or Password is incorrect");
    }
  };

  return (
    <section className="relative overflow-hidden bg-web-background min-h-screen flex items-center justify-center lg:px-24 md:px-12 px-6 md:py-16">

      {/* Background radial glow */}
      <div className="absolute inset-0 bg-web-radial-glow" />

      {/* Green blur ball */}
      <div className="absolute left-1/2 top-1/2 h-[40vw] w-[40vw] min-h-[250px] min-w-[250px] max-h-[500px] max-w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-web-primary/20 blur-[140px]" />

      <div className="relative z-10 w-full max-w-md flex flex-col items-center">

        {/* Lock icon badge */}
        <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/10 shadow shadow-web-radial-glow">
          <img
            src="/images/lock.svg"
            alt="Lock"
            className="h-6 w-6"
          />
        </div>

        <h2 className="text-4xl font-bold leading-tight text-white md:text-5xl text-center">
          Welcome{" "}
          <span className="text-web-primary">Back.</span>
        </h2>

        <p className="mt-3 text-sm leading-6 text-white/70 text-center">
          Enter your credential to access the console.
        </p>

        {/* Card */}
        <div className="mt-10 w-full rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm px-8 py-8 shadow-2xl">

          <div className="space-y-5" onKeyDown={(e) => e.key === "Enter" && handleLogin()}>

            {/* Email / Mobile */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium uppercase tracking-widest text-white">
                Email or Mobile
              </label>
              <div className="relative">
                <img
                  src="/images/user.svg"
                  alt="Lock"
                  className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2"
                />

                <input
                  type="text"
                  name="emailOrMobile"
                  placeholder="john@company.com"
                  value={form.emailOrMobile}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="w-full rounded-lg bg-white/5 border border-white/10 pl-11 pr-4 py-3 text-sm text-white placeholder:text-white/25
                    focus:outline-none focus:border-web-primary/60 focus:ring-1 focus:ring-web-primary/40
                    disabled:opacity-50 transition"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-medium uppercase tracking-widest text-white">
                  Password
                </label>
                <button
                  type="button"
                  className="text-xs text-web-primary hover:text-web-primary/80 transition"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <img
                  src="/images/key.svg"
                  alt="Key"
                  className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2"
                />

                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={handleChange}
                  disabled={isLoading}
                  className="w-full rounded-lg bg-white/5 border border-white/10 pl-11 pr-11 py-3 text-sm text-white placeholder:text-white/25
      focus:outline-none focus:border-web-primary/60 focus:ring-1 focus:ring-web-primary/40
      disabled:opacity-50 transition"
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition"
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            {/* Remember device */}
            <label className="flex cursor-pointer items-center gap-3 select-none">
              <div
                onClick={() => setRememberDevice((p) => !p)}
                className={`h-4 w-4 rounded border flex items-center justify-center transition
                  ${rememberDevice
                    ? "bg-web-primary border-web-primary"
                    : "border-white/20 bg-white/5"
                  }`}
              >
                {rememberDevice && (
                  <svg viewBox="0 0 10 8" className="h-2.5 w-2.5 fill-none stroke-[#0d1a0f] stroke-2">
                    <polyline points="1 4 3.5 6.5 9 1" />
                  </svg>
                )}
              </div>
              <span className="text-sm text-white/50">Remember this device</span>
            </label>

            {/* Sign In */}
            <button
              onClick={() => handleLogin()}
              disabled={isLoading}
              className="w-full rounded-lg bg-web-primary py-3 text-sm font-semibold text-[#0d1a0f]
                hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Signing in…" : "Sign In"}
            </button>

          </div>

          {/* Footer link */}
          <p className="mt-6 text-center text-sm text-white">
            Don't have an account?{" "}
            <Link to="/register" className="text-web-primary hover:text-web-primary/80 transition">
              Create Account
            </Link>
          </p>
        </div>
      </div>
    </section>
  );
}